/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2023, Zumper
 * @description PinMarker
 */

import { useDisclosure } from '@chakra-ui/hooks'
import { useHover } from '@zcode-ui/hook'
import type { Listable, Pin, RawPinInventorySearch } from '@zcode/state-type'
import React from 'react'

import { PinMarkerDesktop } from '../PinMarkerDesktop'
import { PinMarkerMobile } from '../PinMarkerMobile'
import type { MixpanelMaplistClickType } from '../../../../types'

export interface PinMarkerGroupProps {
  isDot: boolean
  isHovered: boolean
  isMobile: boolean
  isVisited: boolean
  lat?: number
  listables: ReadonlyArray<{
    pin: Listable | Pin<RawPinInventorySearch>
    showPriceMarker: boolean
  }>
  lng?: number
  mixpanelMaplistClick: ({
    buttonClicked,
    entity,
    pageName,
    section,
  }: MixpanelMaplistClickType) => void
  priceTxt: string
  onClick: () => void
  onMouseOut: () => void
  onMouseOver: () => void
}

export const PinMarkerGroup = ({
  isDot,
  isHovered,
  isMobile,
  isVisited,
  lat,
  listables,
  lng,
  mixpanelMaplistClick,
  priceTxt,
  onClick,
  onMouseOut,
  onMouseOver,
}: PinMarkerGroupProps) => {
  const [markerRef, isMarkerHovered] = useHover<HTMLDivElement>()
  const { isOpen, onClose, onToggle } = useDisclosure()

  const hovered = isMarkerHovered || isHovered

  const handlePinClick = () => {
    onClick()
    onToggle()
  }

  if (isMobile) {
    return (
      <PinMarkerMobile
        handlePinClick={handlePinClick}
        isActive={isOpen}
        isDot={isDot}
        isVisited={isVisited}
        lat={lat}
        lng={lng}
        markerRef={markerRef}
        mixpanelMaplistClick={mixpanelMaplistClick}
        onClose={onClose}
        onMouseOut={onMouseOut}
        onMouseOver={onMouseOver}
        previewListable={listables}
        priceTxt={priceTxt}
      />
    )
  }

  return (
    <PinMarkerDesktop
      handlePinClick={handlePinClick}
      hovered={hovered}
      isActive={isOpen}
      isDot={isDot}
      isVisited={isVisited}
      lat={lat}
      lng={lng}
      markerRef={markerRef}
      mixpanelMaplistClick={mixpanelMaplistClick}
      onClose={onClose}
      onMouseOut={onMouseOut}
      onMouseOver={onMouseOver}
      previewListable={listables}
      priceTxt={priceTxt}
    />
  )
}
